<template>
  <div v-if="allRows.length > 0" class="product-attributes">
    <div v-if="highlights.length > 0" class="pa-highlights mb-4">
      <h3 class="pa-title fs-4">{{ highlightsTitle }}</h3>
      <div class="pa-highlight-grid">
        <div v-for="row in highlights" :key="'h-' + row.key" class="pa-highlight-card">
          <span class="pa-highlight-icon"><i :class="row.icon"></i></span>
          <span class="pa-highlight-label">{{ row.label }}</span>
          <span class="pa-highlight-value">{{ row.value }}</span>
        </div>
      </div>
    </div>

    <div class="pa-spec">
      <h3 class="pa-title fs-4">{{ specTitle }}</h3>
      <div class="pa-spec-table">
        <div v-for="row in allRows" :key="row.key" class="pa-spec-row">
          <span class="pa-spec-label">
            <i :class="row.icon"></i>
            {{ row.label }}
          </span>
          <span class="pa-spec-value">{{ row.value }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { mapAttributesToDisplay } from '@/helpers/productAttributes';

const props = defineProps({
  attributes: {
    type: Object,
    default: () => ({}),
  },
  definitions: {
    type: Array,
    default: () => [],
  },
  highlightsTitle: {
    type: String,
    default: 'Destacados',
  },
  specTitle: {
    type: String,
    default: 'Ficha tecnica',
  },
});

const mapped = computed(() =>
  mapAttributesToDisplay({ attributes: props.attributes }, props.definitions),
);

const highlights = computed(() => mapped.value.highlights);
const allRows = computed(() => mapped.value.allRows);
</script>

<style scoped>
.product-attributes {
  margin-top: 0.5rem;
}

.pa-title {
  text-transform: uppercase;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.75rem;
  letter-spacing: 0.02em;
  font-size: clamp(1.05rem, 1.1vw, 1.25rem);
}

.pa-highlight-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.65rem;
}

.pa-highlight-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.75rem 0.85rem;
  display: grid;
  grid-template-columns: 34px 1fr;
  grid-template-rows: auto auto;
  gap: 0.1rem 0.65rem;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.28);
  min-width: 0;
}

.pa-highlight-icon {
  grid-row: 1 / span 2;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-light);
  background: rgba(220, 38, 38, 0.18);
}

.pa-highlight-label {
  font-size: clamp(0.74rem, 0.9vw, 0.82rem);
  font-weight: 600;
  color: #b5b5b5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pa-highlight-value {
  font-size: clamp(0.95rem, 1vw, 1.05rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.pa-spec-table {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.03);
}

.pa-spec-row {
  display: grid;
  grid-template-columns: minmax(180px, 36%) minmax(0, 1fr);
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  align-items: center;
}

.pa-spec-row:last-child {
  border-bottom: none;
}

.pa-spec-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.03);
}

.pa-spec-label {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-size: clamp(0.85rem, 0.95vw, 0.95rem);
  font-weight: 600;
  color: #e5e5e5;
}

.pa-spec-label i {
  width: 20px;
  color: var(--primary-light);
  text-align: center;
}

.pa-spec-value {
  font-size: clamp(0.88rem, 0.95vw, 1rem);
  color: #f2f2f2;
  word-break: break-word;
}

@media (max-width: 576px) {
  .pa-spec-row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
    align-items: start;
  }
}
</style>
